import type { GraphId } from './common';

export type TemporalStance =
  | 'alleged_past'
  | 'alleged_ongoing'
  | 'alleged_future'
  | 'hypothetical'
  | 'conditional'
  | 'resolved_true'
  | 'resolved_false'
  | 'disputed'
  | 'retracted'
  | string;

export type EventCategory =
  | 'all'
  | 'conflict'
  | 'economy'
  | 'politics'
  | 'science'
  | 'tech'
  | 'climate';

export type EventSort = 'activity' | 'recency' | 'confidence';

export interface BackendEventListItem {
  id: GraphId;
  lat: number;
  lng: number;
  source: string;
  source_service_id?: GraphId;
  author_ids?: GraphId[];
  confidence: number;
  probability?: number;
  activity: number;
  volatility: number;
  category: string;
  headline: string;
  location: string;
  time_ago: string;
  temporal_stance?: TemporalStance;
  validation_status?: string;
  veracity_elo?: number | null;
  precision?: number | null;
}

export interface PublicEventsResponse {
  viewer: 'public_baseline';
  events: BackendEventListItem[];
}

export interface PublicEventResponse {
  viewer: 'public_baseline';
  event: unknown;
  visible_claim_count: number;
  visible_source_count: number;
}

export interface ChaocEvent {
  id: GraphId;
  lat: number;
  lng: number;
  source: string;
  sourceServiceId?: GraphId;
  authorIds: GraphId[];
  confidence: number;
  probability: number;
  activity: number;
  volatility: number;
  category: Exclude<EventCategory, 'all'>;
  headline: string;
  location: string;
  timeAgo: string;
  temporalStance?: TemporalStance;
  validationStatus?: string;
  veracityElo?: number | null;
  precision?: number | null;
  userFlag?: boolean;
}

function normalizeRatio(value: number | undefined, fallback = 0): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return fallback;
  if (value > 1) return Math.max(0, Math.min(1, value / 100));
  return Math.max(0, Math.min(1, value));
}

function normalizeCategory(value: string | undefined): ChaocEvent['category'] {
  const normalized = (value || 'politics').toLowerCase();
  if (
    normalized === 'conflict' ||
    normalized === 'economy' ||
    normalized === 'politics' ||
    normalized === 'science' ||
    normalized === 'tech' ||
    normalized === 'climate'
  ) {
    return normalized;
  }
  return 'politics';
}

export function normalizeBackendEvent(event: BackendEventListItem): ChaocEvent {
  const confidence = normalizeRatio(event.confidence);
  return {
    id: event.id,
    lat: event.lat,
    lng: event.lng,
    source: event.source,
    sourceServiceId: event.source_service_id,
    authorIds: event.author_ids || [],
    confidence,
    probability: normalizeRatio(event.probability, confidence),
    activity: normalizeRatio(event.activity),
    volatility: normalizeRatio(event.volatility),
    category: normalizeCategory(event.category),
    headline: event.headline,
    location: event.location,
    timeAgo: event.time_ago,
    temporalStance: event.temporal_stance,
    validationStatus: event.validation_status,
    veracityElo: event.veracity_elo,
    precision: event.precision,
  };
}
